import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/dialog";

const IMG = "/img/heroes";

const roles = [
  {
    img: `${IMG}/role-paramedics.jpg`,
    label: "Paramedics & EMS",
    text: "The first face people see on the worst day of their life. Long shifts, late calls and no guarantee of a meal break. You look after everyone else, so let us look after your travel money.",
  },
  {
    img: `${IMG}/role-firefighters.jpg`,
    label: "Firefighters",
    text: "Structure fires, wildfire season, rescues and medical calls. You're the ones heading in when everyone else is heading out, and that deserves more than a thank-you card.",
  },
  {
    img: `${IMG}/role-law.jpg`,
    label: "Law Enforcement",
    text: "The officers who keep our streets, courts and communities safe, day and night, in every kind of weather. Whatever your badge says, if you serve in law enforcement, you qualify.",
  },
  {
    img: `${IMG}/role-dispatch.jpg`,
    label: "911 Dispatchers",
    text: "The calm voice on the other end of the line. You're the first responder nobody sees, holding it together through someone's worst minutes and sending help on its way.",
  },
  {
    img: `${IMG}/role-nurses.jpg`,
    label: "Nurses",
    text: "Twelve-hour shifts, short staffing and still the kindest person in the room. From the ward to the ER to home care, nurses keep our hospitals running.",
  },
  {
    img: `${IMG}/role-doctors.jpg`,
    label: "ER Doctors",
    text: "The emergency department never closes, and neither do you. When the ambulance doors open, you're the team that takes it from there.",
  },
  {
    img: `${IMG}/role-teachers.jpg`,
    label: "Teachers",
    text: "You shape the next generation every single day, often with your own money and on your own time. Planning a trip over the summer or spring break? You've earned a better rate.",
  },
];

const steps = [
  {
    title: "Bring your work ID",
    text: "Visit us at the counter with your current work ID or badge. We check it once and note it on your customer file. That's it.",
  },
  {
    title: "Your Heroes Rate switches on",
    text: "From then on, our system knows you're a Frontline Hero. Every time you exchange with us, the Heroes Rate is applied automatically. No codes to remember, no need to ask, no awkward conversation.",
  },
  {
    title: "See what you saved",
    text: "Your receipt shows the Heroes Rate you received and exactly how much you saved compared to our regular board. Nothing hidden, nothing in the fine print.",
  },
];

const expect = [
  {
    tag: "Heroes Rate",
    dot: "bg-emerald-400",
    title: "Better than our own board",
    text: "On the currencies we have in the drawer where there's room to do better, you get a rate better than the one on our board. Our board is already set to match or beat other exchanges nearby, so this is on top of that.",
  },
  {
    tag: "Board rate",
    dot: "bg-[#3B82F6]",
    title: "Our best posted rate",
    text: "Some currencies are already priced as sharp as we can go. On those, you get our board rate, which is still one of the most competitive in the Lower Mainland. You're never charged more than anyone else.",
  },
  {
    tag: "Special order",
    dot: "bg-amber-400",
    title: "We'll order it in for you",
    text: "Heading somewhere less common? If we don't have the currency on hand, we'll order it. Special orders are priced at the market on the day we order, plus what our supplier charges us, and you get a firm quote before you commit. If there's room, your Heroes Rate is included.",
  },
];

const faqs = [
  {
    q: "Why don't you post the Heroes Rates online?",
    a: "Heroes Rates are personal to you and they move with the currency market during the day. A number posted on a website is out of date within hours. We'd rather give you the real rate at the counter. If you'd like to know before you come in, give us a call and we'll tell you today's rate over the phone.",
  },
  {
    q: "Why isn't there a Heroes Rate on every currency?",
    a: "It depends on what each currency cost us to bring in and what we can sell it for. Where our cost leaves room, you get a better rate automatically. Where it doesn't, giving more would mean selling below cost, so you get our board rate, which is already one of the best around. This can change as the market moves and as we restock.",
  },
  {
    q: "Is the Heroes Rate better than other exchanges?",
    a: "The Heroes Rate is always at or better than our own board, and our board is set to match or beat local exchanges. If you've seen a better rate somewhere else, tell us at the counter and we'll take a look.",
  },
  {
    q: "Is there a limit?",
    a: "The Heroes Rate is built for everyday amounts: a family trip, a visit home, spending money for a holiday. It covers up to CAD 3,000 a day. Exchanging more for a big move or a long trip? Talk to us and we'll put together a quote.",
  },
  {
    q: "Do I need to bring my ID every time?",
    a: "No. Once is enough. We note it on your customer file and you're set for every visit after that. We may ask again from time to time, or if your job changes.",
  },
  {
    q: "I'm retired or a volunteer. Do I qualify?",
    a: "The program is for people currently working in one of the roles on this page. Retired or volunteer? Come and talk to us anyway. We'd love to hear your story.",
  },
  {
    q: "Can I use it for buying and selling?",
    a: "Yes. The Heroes Rate works both ways: when you buy foreign currency before a trip, and when you sell your leftover cash back to us when you get home.",
  },
  {
    q: "Can I send a family member in with my rate?",
    a: "The Heroes Rate is for you, in person, as the qualifying customer. Bring them along and exchange together.",
  },
];

const conditions = [
  "The Frontline Heroes Rate (the \"Heroes Rate\") is offered by FonderX Currency Exchange Inc. for cash currency exchange made in person at our office, Unit 2 – 723 12th Street, New Westminster, BC.",
  "It is available to people currently employed as paramedics or EMS staff, firefighters, law enforcement officers, 911 dispatchers, nurses, emergency room doctors or teachers. Retired staff and volunteers are not included.",
  "To join, show a valid work ID or badge at the counter once. We note it on your customer file. We may ask to see it again at any time, including if your role changes.",
  "The Heroes Rate is personal to you and can't be transferred. You must be at the counter, and the exchange must be in your name.",
  "Not every currency has a Heroes Rate. Whether one is offered depends on our cost of the notes on hand, what we can resell them for, and the market, and can change at any time. Where offered, it is always at or better than our posted board rate. On other currencies you receive our board rate.",
  "Rates move with the market and are not posted online. The rate is set at the time of your exchange, and the rate shown on your receipt is final.",
  "The Heroes Rate is intended for everyday amounts, up to CAD 3,000 per customer per day (buying and selling combined). Larger amounts are quoted separately.",
  "Currencies we don't have on hand can be special ordered. Special orders are priced at the market on the day we order plus our supplier's cost, and you receive a quote before you commit. A Heroes Rate applies to a special order only where there is room.",
  "The usual identification and record-keeping requirements under Canadian law still apply to every exchange, whatever the amount.",
  "The Heroes Rate has no cash value and applies only to currency exchange.",
  "FonderX may refuse, change, suspend or end the program, or any customer's participation in it, at any time without notice.",
];

export default function FrontlineHeroes() {
  return (
    <div className="min-h-screen" data-testid="heroes-page">
      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="trust-badge mb-6">
                <span>Frontline Heroes Rate</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6">
                Better rates for the people who <span className="text-[#3B82F6]">show up for us</span>
              </h1>
              <p className="text-xl text-blue-100/70 leading-relaxed mb-5">
                Paramedics, firefighters, law enforcement, 911 dispatchers, nurses, ER doctors and teachers:
                you give our community more than anyone could ever pay back.
              </p>
              <p className="text-lg text-blue-100/60 leading-relaxed mb-8">
                So when you exchange currency at FonderX, you get a better rate than our already-competitive
                board. Show your work ID once, and it's yours on every visit after that.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                  Visit Our Office
                </Link>
                <a href="tel:+17787590400" className="btn-secondary inline-flex items-center gap-2">
                  Call for today's rate
                </a>
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <button type="button" className="mt-4 text-xs text-blue-100/50 underline underline-offset-2 hover:text-blue-100/80" data-testid="conditions-open">
                    Conditions apply
                  </button>
                </DialogTrigger>
                <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[85vh] overflow-y-auto bg-[#001a40] border-white/10 text-white rounded-xl p-6 sm:p-8 [&>button]:!bg-transparent [&>button]:!text-white/70" data-testid="conditions-dialog">
                  <DialogHeader className="text-left">
                    <DialogTitle className="text-2xl font-bold text-white">Program Conditions</DialogTitle>
                    <DialogDescription className="text-blue-100/50">Frontline Heroes Rate · FonderX Currency Exchange Inc.</DialogDescription>
                  </DialogHeader>
                  <ol className="list-decimal pl-5 space-y-3 text-blue-100/70 text-sm leading-relaxed marker:text-blue-100/40">
                    {conditions.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ol>
                  <div className="flex justify-end pt-2">
                    <DialogClose asChild>
                      <button type="button" className="btn-primary">Close</button>
                    </DialogClose>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img src={`${IMG}/hero-paramedics.jpg`} alt="A BC Ambulance on a Vancouver street" className="rounded-2xl w-full h-64 sm:h-80 object-cover" />
                <img src={`${IMG}/hero-firefighters.jpg`} alt="Vancouver firefighters at a call" className="rounded-2xl w-full h-40 sm:h-52 object-cover" />
              </div>
              <div className="space-y-4 pt-10">
                <img src={`${IMG}/hero-nurse.jpg`} alt="A smiling nurse in a hospital hallway" className="rounded-2xl w-full h-40 sm:h-52 object-cover" />
                <img src={`${IMG}/hero-police.jpg`} alt="RCMP officers on duty" className="rounded-2xl w-full h-64 sm:h-80 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our commitment */}
      <section className="bg-[#001a40] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <img src={`${IMG}/story-fire.jpg`} alt="A fire engine and a police car on a Vancouver street" className="rounded-2xl w-full h-72 md:h-[420px] object-cover" />
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Our Commitment to the Front Line</h2>
              <div className="space-y-4 text-blue-100/70 leading-relaxed">
                <p>
                  FonderX is an independent, locally owned currency exchange in New Westminster. Our community
                  relies every day on the people who answer the call: at three in the morning, on long weekends,
                  and on the holidays when everyone else is at home. They rarely hear the words "thank you".
                </p>
                <p>
                  The Frontline Heroes Rate is our way of saying it properly. Not with a one-day promotion or
                  a coupon that expires, but with a better rate every time you walk through our door.
                </p>
                <p>
                  When you finally get that week off, whether it's a trip back home to see family or the
                  holiday you've been promising yourself, more of your money should end up in your pocket.
                  That's the whole idea.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who qualifies */}
      <section className="bg-[#002055] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Who Qualifies</h2>
            <p className="text-blue-200/70 max-w-2xl mx-auto">
              If you currently work in one of these roles and can show a valid work ID,
              the Heroes Rate is yours.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {roles.map((r, i) => (
              <div key={i} className="bg-white/5 backdrop-blur rounded-xl border border-white/10 hover:border-[#3B82F6]/30 transition-colors overflow-hidden flex flex-col">
                <img src={r.img} alt={r.label} className={`w-full h-48 object-cover ${r.pos || ""}`} loading="lazy" />
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-white mb-2">{r.label}</h3>
                  <p className="text-blue-100/60 text-sm leading-relaxed">{r.text}</p>
                </div>
              </div>
            ))}
            <div className="bg-[#3B82F6]/10 rounded-xl border border-[#3B82F6]/30 p-6 flex flex-col justify-center">
              <h3 className="text-lg font-semibold text-white mb-3">Not sure if you qualify?</h3>
              <p className="text-blue-100/70 text-sm leading-relaxed mb-5">
                Work alongside these teams in a role that isn't listed? Give us a call or ask at the counter.
                We'll always hear you out.
              </p>
              <a href="tel:+17787590400" className="text-[#3B82F6] font-semibold text-sm hover:underline">
                +1 (778) 759-0400
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#001435] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How It Works</h2>
              <p className="text-blue-200/70 mb-10">Three steps, one visit. After that, it just happens.</p>
              <div className="space-y-8">
                {steps.map((s, i) => (
                  <div key={i} className="flex gap-5">
                    <div className="w-10 h-10 rounded-full bg-[#3B82F6] flex items-center justify-center text-white font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">{s.title}</h3>
                      <p className="text-blue-100/60 leading-relaxed">{s.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <img src={`${IMG}/how-firefighters.jpg`} alt="Firefighters running together on a Vancouver street" className="rounded-2xl w-full h-72 md:h-[460px] object-cover" />
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="bg-[#002055] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">What to Expect at the Counter</h2>
              <p className="text-blue-200/70 leading-relaxed mb-8">
                Your Heroes Rate is given in person, at today's market. Currency prices change through the
                day, and the notes we have on hand change too. Here's how it works on each currency, so
                there are no surprises.
              </p>
              <img src={`${IMG}/counter-money.jpg`} alt="Canadian banknotes on a table" className="rounded-2xl w-full h-56 md:h-72 object-cover" />
            </div>
            <div className="lg:col-span-3 space-y-5">
              {expect.map((e, i) => (
                <div key={i} className="bg-white/5 backdrop-blur rounded-xl p-6 sm:p-8 border border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`w-2.5 h-2.5 rounded-full ${e.dot}`} />
                    <span className="text-xs font-semibold uppercase tracking-wider text-blue-100/60">{e.tag}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{e.title}</h3>
                  <p className="text-blue-100/60 leading-relaxed">{e.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 bg-white/5 backdrop-blur rounded-2xl p-6 sm:p-10 border border-white/10">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
              <div className="lg:col-span-3">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-5">Why not every currency gets a Heroes Rate</h3>
                <div className="space-y-4 text-blue-100/70 leading-relaxed">
                  <p>
                    Every banknote in our drawer has a cost. When you buy a currency from us, our price depends on
                    what it cost us to bring those notes in, whether we bought them from another customer or had
                    them shipped to Canada by a wholesale supplier. When you sell a currency to us, our price
                    depends on what we can resell it for.
                  </p>
                  <p>
                    Some currencies we bring in at a good cost and trade often, so there's room to give you a
                    better rate. Others cost more to bring in, move slowly, or are already priced as sharp as we
                    can go. On those, lowering the price further would mean selling below our cost, so you get our
                    board rate instead, which is still one of the most competitive around.
                  </p>
                  <p>
                    It can also change from week to week. The same currency can have a Heroes Rate one week and
                    not the next as the market moves and as we restock at a different cost. That's why we give
                    the Heroes Rate at the counter, on the day.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-100/60 mb-4">What it depends on</p>
                <ul className="space-y-4">
                  {[
                    ["Our buy cost", "What we paid for the notes we have on hand today."],
                    ["Our sell price", "What we can resell a currency for once you've sold it to us."],
                    ["The market", "Today's exchange rate, which moves throughout the day."],
                    ["Supply and demand", "How often we trade that currency and how easy it is to restock."],
                  ].map(([t, d], i) => (
                    <li key={i} className="border-l-2 border-[#3B82F6]/60 pl-4">
                      <p className="text-white font-semibold">{t}</p>
                      <p className="text-blue-100/60 text-sm leading-relaxed">{d}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#001435] py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Questions Heroes Ask</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="group bg-white/5 rounded-xl border border-white/10 px-6 py-4" open={i === 0}>
                <summary className="flex items-center justify-between cursor-pointer list-none text-white font-semibold">
                  {f.q}
                  <ChevronDown className="w-5 h-5 text-[#3B82F6] transition-transform group-open:rotate-180 flex-shrink-0 ml-4" />
                </summary>
                <p className="text-blue-100/60 text-sm leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Call to action */}
      <section
        className="relative py-24 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMG}/cta-newwest.jpg)` }}
      >
        <div className="absolute inset-0 bg-[#001435]/80" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Come see your rate</h2>
          <p className="text-blue-100/80 leading-relaxed mb-8 max-w-2xl mx-auto">
            Bring your work ID and we'll set you up in a minute. We're at Unit 2 – 723 12th Street in New
            Westminster, open Monday to Friday, 9:30 AM to 6 PM.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
              Get directions
            </Link>
            <a href="tel:+17787590400" className="btn-secondary inline-flex items-center gap-2">
              +1 (778) 759-0400
            </a>
          </div>
          <p className="text-blue-100/40 text-xs mt-10">Photos: Unsplash and Pexels</p>
        </div>
      </section>
    </div>
  );
}
